/**
 * CTE AI Feedback Panel
 * Displays AI-generated insight after a thermal analysis run.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import './CTEAIPanel.css';

function markdownToHTML(md) {
  let html = md
    .replace(/#### (.+)/g, '<h4>$1</h4>')
    .replace(/### (.+)/g, '<h3>$1</h3>')
    .replace(/## (.+)/g, '<h2>$1</h2>')
    .replace(/# (.+)/g, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n{2,}/g, '\n\n');

  const blocks = html.split('\n\n');
  const result = [];

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('<h')) {
      result.push(trimmed);
      continue;
    }

    const lines = trimmed.split('\n');
    const isList = lines.every(l => /^[-*]\s/.test(l.trim()));
    const isNumberedList = lines.every(l => /^\d+\.\s/.test(l.trim()));

    if (isList) {
      const items = lines.map(l => `<li>${l.replace(/^[-*]\s+/, '').trim()}</li>`).join('');
      result.push(`<ul>${items}</ul>`);
    } else if (isNumberedList) {
      const items = lines.map(l => `<li>${l.replace(/^\d+\.\s+/, '').trim()}</li>`).join('');
      result.push(`<ol>${items}</ol>`);
    } else {
      result.push(`<p>${trimmed.replace(/\n/g, '<br/>')}</p>`);
    }
  }

  return result.join('');
}

async function fetchAIFeedback(analysisData, endpoint = '/api/cte/ai/analyse') {
  const bodyKey = endpoint.includes('cycle-analysis') ? 'cycleData' : 'analysisData';
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ [bodyKey]: analysisData }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Unknown error');
  return data.feedback;
}

export default function CTEAIPanel({ analysisData, label, endpoint, onFeedback, autoRun }) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const prevDataRef = useRef(null);

  const requestFeedback = useCallback(async () => {
    if (!analysisData) return;
    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      const result = await fetchAIFeedback(analysisData, endpoint);
      setFeedback(result);
      setExpanded(true);
      if (onFeedback) onFeedback(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [analysisData, endpoint, onFeedback]);

  useEffect(() => {
    if (!autoRun || !analysisData) return;
    const dataStr = JSON.stringify(analysisData);
    if (dataStr !== prevDataRef.current) {
      prevDataRef.current = dataStr;
      requestFeedback();
    }
  }, [autoRun, analysisData, requestFeedback]);

  const hasFeedback = feedback || loading || error;

  return (
    <div className="cte-ai-panel">
      <div className="cte-ai-panel-header" onClick={() => hasFeedback && setExpanded(e => !e)}>
        <div className="cte-ai-panel-title">
          <span className="cte-ai-panel-title-icon">AI</span>
          <span>{label || 'AI Insight'}</span>
          {hasFeedback && !expanded && (
            <span className="cte-ai-collapsed-hint">Click to expand</span>
          )}
        </div>
        <div className="cte-ai-panel-actions">
          <button
            className="cte-ai-btn"
            onClick={(e) => { e.stopPropagation(); requestFeedback(); }}
            disabled={loading || !analysisData}
          >
            {loading ? 'Analysing...' : feedback ? 'Re-analyse' : 'Get AI Feedback'}
          </button>
        </div>
      </div>

      {expanded && hasFeedback && (
        <div className="cte-ai-panel-body">
          {loading && (
            <div className="cte-ai-loading">
              <div className="cte-ai-loading-dot">
                <span></span><span></span><span></span>
              </div>
              Analysing your results...
            </div>
          )}
          {error && <div className="cte-ai-error">Failed to get AI feedback: {error}</div>}
          {feedback && (
            <div
              className="cte-ai-feedback"
              dangerouslySetInnerHTML={{ __html: markdownToHTML(feedback) }}
            />
          )}
        </div>
      )}
    </div>
  );
}
